import { Alpine } from '../core/alpine-core.js';

import '../core/alpine-directives.js';
import {
  ADMIN_LOGIN_ABS,
  absoluteAdminPathnameFromPineconePath,
  installRouteGuards,
  isAdminShellPublicPath,
  sanitizePostLoginNext,
} from './router-guards.js';
import { registerCoreStores, registerRoleDirectives, registerSessionStore } from '../core/stores.js';
import { registerThemeStore } from '../core/theme.js';
import { registerNavController } from '../core/nav-core.js';
import { registerMobileNav } from '../core/mobile-nav.js';
import { registerHeaderController } from '../core/header-controller.js';
import { setupIcons } from '../core/icons.js';
import { registerFeatureStore } from '../core/features-store.js';
import { stripLeagueFromAdminPath } from './league-context.js';
import { apiFetch } from './api.js';


// Base URL for x-include on admin
window.__BASE_URL__ = '/admin/';

// Make UI store before routes use it
registerCoreStores();
registerSessionStore();
registerRoleDirectives();
registerFeatureStore();

registerThemeStore();
try {
  globalThis.localStorage?.setItem('theme', 'dark');
} catch { }
document.documentElement.classList.add('dark');
document.documentElement.style.colorScheme = 'dark';

registerNavController();
registerMobileNav();
registerHeaderController();

setupIcons();

// Keep UI store in sync with current route
const ADMIN_BASE = '/admin';
const canonicalPath = stripLeagueFromAdminPath(location.pathname);
const initPath = canonicalPath.startsWith(ADMIN_BASE)
  ? canonicalPath.slice(ADMIN_BASE.length) || '/'
  : '/';
Alpine.store('ui').setRoute(initPath);

// Close drawers & toggle chrome per route (use absolute dest path — `detail.path` is often `/login` not `/admin/login`)
document.addEventListener('pinecone:before', (e) => {
  const path = stripLeagueFromAdminPath(e.detail?.path || '/');
  Alpine.store('ui').setRoute(path);
  const absDest = absoluteAdminPathnameFromPineconePath(e.detail?.path || '/');
  Alpine.store('ui').setHideChrome(isAdminShellPublicPath(absDest));
});

// Admin components (apps/pages)
import './components.js';

import '../core/toastr.js';
import { registerChallengeNotifications } from './challenge-notifications.js';

registerChallengeNotifications();

import './router.js';

(async function bootAdmin() {
  /** Before any async work — avoids leagues header + x-include flash while `/auth/me` is in flight. */
  Alpine.store('ui').setHideChrome(isAdminShellPublicPath());

  const S = Alpine.store('session');
  await S.ensureFreshMe(true);
  if (S?.token && (!S.me || !S.me.role)) {
    await S.refreshMe();
  }
  if (S?.token && (!S.me || !S.me.role)) {
    S.clear();
    if (!isAdminShellPublicPath()) {
      location.href = `${ADMIN_LOGIN_ABS}?reason=role-missing`;
      return;
    }
  }

  Alpine.store('ui').setHideChrome(isAdminShellPublicPath());
  const onLoginPage = (() => {
    try {
      const p = String(location.pathname || '').replace(/\/+$/, '') || '/';
      return p === '/admin/login' || p === '/login';
    } catch {
      return false;
    }
  })();
  if (S.isAuthed && onLoginPage) {
    const from = sanitizePostLoginNext(`${location.pathname}${location.search}${location.hash}`);
    location.replace(from);
    return;
  }
  if (!S.isAuthed && !isAdminShellPublicPath()) {
    const from = sanitizePostLoginNext(`${location.pathname}${location.search}${location.hash}`);
    sessionStorage.setItem('NOAUTH_FROM', from);
    location.replace(`${ADMIN_LOGIN_ABS}?next=${encodeURIComponent(from)}`);
    return;
  }

  if (S?.token) {
    try {
      const res = await apiFetch('/api/admin/account-settings');
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        Alpine.store('subscriptionGate')?.setFromAccountSettings?.(j);
      }
    } catch {
      /* keep Vite defaults */
    }
    Alpine.store('challengeNotifications')?.init?.();
  }
  installRouteGuards();
  Alpine.start();
})();
